import { Text } from '@lmc/design-system'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'
import type { IncidentCaseV2ReadPanelProps } from '../../incidentCaseV2Steps'
import { STATUS_LABEL_KEYS } from '../../steps/followUpActions/FollowUpActionDrawer'
import { ReadEntry, ReadItem, ReadWide } from '../readLayout'
import { actionDueDate, savedFollowUpActions } from '../readRules'
import { formatDateTime } from '../readValues'
import { useTeamMemberName } from '../usePersonName'

const testId = 'incident-case-v2-view-follow-up-actions'

/** Step 6, read only: one block per action, as the form's card shows it. */
const FollowUpActionsReadPanel = ({
  incidentReport
}: IncidentCaseV2ReadPanelProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const { isLoading, nameOf } = useTeamMemberName()
  const actions = savedFollowUpActions(incidentReport)

  return (
    <>
      {actions.map((action, index) => {
        const dueDate = actionDueDate(t, action)
        const statusKey = STATUS_LABEL_KEYS[action.status]
        return (
          <ReadEntry
            // Ids are minted again on every save, so blocks are keyed by place.
            // biome-ignore lint/suspicious/noArrayIndexKey: no stable id
            key={index}
            title={t(incidentReportsConstants.CASE_V2_VIEW_ACTIONS_ACTION, {
              number: index + 1,
              title: action.title
            })}
            testId={`${testId}-action-${index}`}
          >
            <ReadWide>
              <ReadItem
                label={t(incidentReportsConstants.CASE_V2_VIEW_ACTIONS_DETAILS)}
                value={action.details}
                testId={`${testId}-action-${index}-details`}
              />
            </ReadWide>
            <ReadItem
              isLoading={isLoading}
              label={t(incidentReportsConstants.CASE_V2_ACTIONS_OWNER)}
              value={nameOf(action.assignedUserId)}
              testId={`${testId}-action-${index}-owner`}
            />
            <ReadItem
              label={t(incidentReportsConstants.CASE_V2_ACTIONS_STATUS)}
              value={statusKey ? t(statusKey) : null}
              testId={`${testId}-action-${index}-status`}
            />
            <ReadItem
              label={t(incidentReportsConstants.CASE_V2_ACTIONS_DUE_DATE)}
              value={
                dueDate.isOverdue ? (
                  <Text as="span" tone="critical" weight="medium">
                    {dueDate.text}
                  </Text>
                ) : (
                  dueDate.text
                )
              }
              testId={`${testId}-action-${index}-due-date`}
            />
            <ReadItem
              label={t(
                incidentReportsConstants.CASE_V2_ACTIONS_COMPLETION_TIME
              )}
              value={formatDateTime(action.completedAt)}
              testId={`${testId}-action-${index}-completed-at`}
            />
          </ReadEntry>
        )
      })}
    </>
  )
}

export default FollowUpActionsReadPanel
